import React, { useEffect, useState, useRef } from "react";
import { FilterNodeData, HandleConfig } from "../../types";
import { Position } from "reactflow";
import ConfigBoxToolbar from "../../components/shared/ConfigBoxToolbar";
import LabelWithVisibilityToggle, { VisibilityOption } from "../../components/shared/LabelWithVisibilityToggle";
import AutoComplete, { AutoCompleteOption } from "../../components/shared/AutoComplete";
import HandleManager from "../../components/shared/HandleManager";

// Extended interface for filter suggestions that includes filterType
interface FilterSuggestion extends AutoCompleteOption {
  filterType: string;
}

interface FilterConfigBoxProps {
  nodeId: string;
  data: FilterNodeData;
  onUpdate?: (data: Partial<FilterNodeData>) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onDetachFromGroup?: () => void; // Add prop
  isAdvancedMode?: boolean;
  portalRoot?: HTMLElement | null;
}

const FILTER_SUGGESTIONS: FilterSuggestion[] = [
  { value: "User ID Filter", label: "User ID Filter", filterType: "Exclude anonymous users" },
  { value: "High-Value Customer Filter", label: "High-Value Customer Filter", filterType: "LTV > $1000" },
  { value: "Purchase Event Filter", label: "Purchase Event Filter", filterType: "event_name = 'purchase'" },
  { value: "Cart Abandonment Filter", label: "Cart Abandonment Filter", filterType: "abandoned_cart = true" },
  { value: "Recent Activity Filter", label: "Recent Activity Filter", filterType: "timestamp > 24h" },
  { value: "Conversion Path Filter", label: "Conversion Path Filter", filterType: "conversion_stage = 'completed'" },
  { value: "Geographic Filter", label: "Geographic Filter", filterType: "country IN ('US', 'CA', 'UK')" },
  { value: "Mobile Device Filter", label: "Mobile Device Filter", filterType: "platform = 'iOS' OR platform = 'Android'" },
  { value: "Campaign Attribution Filter", label: "Campaign Attribution Filter", filterType: "utm_source = 'google'" },
  { value: "Engagement Score Filter", label: "Engagement Score Filter", filterType: "engagement_score > 75" },
  { value: "First-Time Visitor Filter", label: "First-Time Visitor Filter", filterType: "is_first_session = true" },
  { value: "Returning Customer Filter", label: "Returning Customer Filter", filterType: "purchase_count > 1" },
  { value: "Product Category Filter", label: "Product Category Filter", filterType: "category = 'electronics'" },
  { value: "Price Range Filter", label: "Price Range Filter", filterType: "price BETWEEN 50 AND 200" },
  { value: "Session Duration Filter", label: "Session Duration Filter", filterType: "session_time > 5min" },
  { value: "Referral Source Filter", label: "Referral Source Filter", filterType: "referrer CONTAINS 'facebook'" },
  { value: "Browser Type Filter", label: "Browser Type Filter", filterType: "browser = 'Chrome'" },
  { value: "A/B Test Group Filter", label: "A/B Test Group Filter", filterType: "test_group = 'B'" },
  { value: "Customer Segment Filter", label: "Customer Segment Filter", filterType: "segment = 'premium'" },
  { value: "Custom Event Property Filter", label: "Custom Event Property Filter", filterType: "properties.custom_value > 100" }
];


export default function FilterConfigBox({
  data,
  nodeId,
  onUpdate,
  onDelete,
  onDuplicate,
  onDetachFromGroup, // Destructure prop
  portalRoot,
  isAdvancedMode = false,
}: FilterConfigBoxProps) {
  // Initialize state with props
  const [name, setName] = useState(data.name || "");
  const [filterType, setFilterType] = useState(data.filterType || "");
  const [notes, setNotes] = useState(data.notes || "");

  const [nameVisibility, setNameVisibility] = useState<VisibilityOption>(
    data.nameVisibility === "hidden" ? "hidden" : "visible"
  );
  const [notesVisibility, setNotesVisibility] = useState<VisibilityOption>(
    data.notesVisibility === "hidden" ? "hidden" : "visible"
  );
  const [filterTypeVisibility, setFilterTypeVisibility] = useState<VisibilityOption>(
    data.filterTypeVisibility === "hidden" ? "hidden" : "visible"
  );

  const defaultHandles: HandleConfig[] = [
    { id: 'default-target', type: 'target', position: Position.Left, color: '#22c55e' },
    { id: 'accepted-output', type: 'source', position: Position.Right, color: '#3b82f6' },
    { id: 'rejected-output', type: 'source', position: Position.Right, color: '#ef4444' },
  ];

  const [handles, setHandles] = useState<HandleConfig[]>(
    Array.isArray(data.handles) ? [...data.handles] : defaultHandles
  );

  const isFirstRender = useRef(true);
  const prevUpdateRef = useRef("");
  const prevPropsRef = useRef("");

  useEffect(() => {
    const incomingProps = JSON.stringify({
      name: data.name || "",
      filterType: data.filterType || "",
      notes: data.notes || "",
      nameVisibility: data.nameVisibility === "hidden" ? "hidden" : "visible",
      notesVisibility: data.notesVisibility === "hidden" ? "hidden" : "visible",
      filterTypeVisibility: data.filterTypeVisibility === "hidden" ? "hidden" : "visible",
      handles: Array.isArray(data.handles) ? data.handles : defaultHandles,
    });

    if (incomingProps !== prevPropsRef.current) {
      prevPropsRef.current = incomingProps;

      setName(data.name || "");
      setFilterType(data.filterType || "");
      setNotes(data.notes || "");
      setNameVisibility(data.nameVisibility === "hidden" ? "hidden" : "visible");
      setNotesVisibility(data.notesVisibility === "hidden" ? "hidden" : "visible");
      setFilterTypeVisibility(data.filterTypeVisibility === "hidden" ? "hidden" : "visible");

      if (Array.isArray(data.handles)) {
        setHandles([...data.handles]);
      } else {
        setHandles(defaultHandles);
      }
    }

    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, [data]);

  useEffect(() => {
    if (isFirstRender.current) return;

    const outgoingUpdate = JSON.stringify({
      name,
      filterType,
      notes,
      nameVisibility,
      filterTypeVisibility,
      notesVisibility,
      handles,
    });

    if (outgoingUpdate !== prevUpdateRef.current && onUpdate) {
      prevUpdateRef.current = outgoingUpdate;
      onUpdate({
        name,
        filterType,
        notes,
        nameVisibility,
        filterTypeVisibility,
        notesVisibility,
        handles,
      });
    }
  }, [
    name,
    filterType,
    notes,
    nameVisibility,
    filterTypeVisibility,
    notesVisibility,
    handles,
    onUpdate
  ]);
  
  // Toggle visibility setting
  const toggleVisibility = (
    current: VisibilityOption,
    setter: React.Dispatch<React.SetStateAction<VisibilityOption>>
  ) => {
    const nextVisibility = current === "hidden" ? "visible" : "hidden";
    setter(nextVisibility);
  };

  return (
    <div className="space-y-3 relative">
      {/* Add the toolbar */}
      <ConfigBoxToolbar
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onDetachFromGroup={onDetachFromGroup} // Pass prop down
        isAdvancedMode={isAdvancedMode}
      />

      <div className="pt-1">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 mt-3">
          Filter Name
        </label>
        <AutoComplete
          value={name}
          onChange={(value: string) => {
            setName(value);
          }}
          onSelect={(option) => {
            if (typeof option === 'object') {
              setName(option.value);
              // Type guard to check if option is a FilterSuggestion
              const isFilterSuggestion = (opt: any): opt is FilterSuggestion => {
                return 'filterType' in opt;
              };
              
              // Set the filterType if it's a FilterSuggestion
              if (isFilterSuggestion(option)) {
                setFilterType(option.filterType);
              }
            } else {
              setName(option);
            }
          }}
          options={FILTER_SUGGESTIONS}
          placeholder="Select or enter filter name"
          dropdownClassName="absolute z-[100]"
          portalContainer={portalRoot}
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Filter Condition"
          visibility={filterTypeVisibility}
          onToggleVisibility={() => toggleVisibility(filterTypeVisibility, setFilterTypeVisibility)}
          className="mb-1"
        />
        <input
          type="text"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          placeholder="Filter condition e.g. 'event_name = purchase'"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Notes"
          visibility={notesVisibility}
          onToggleVisibility={() => toggleVisibility(notesVisibility, setNotesVisibility)}
          className="mb-1"
        />
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes about this filter (optional)"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out resize-y min-h-[80px] max-h-[400px]"
          maxLength={500} // Limit to 500 characters
        />
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
          {notes.length}/500
        </div>
      </div>
      {/* --- Handle Manager --- */}
      <div>
        <HandleManager
          nodeId={nodeId}
          handles={handles}
          onChange={setHandles}
          maxHandles={6}
          portalRoot={portalRoot}
        />
      </div>
    </div>
  );
}
